import { LifestyleProduct, PaymentFrequency, Pricing } from "CommonTypes";

export const debounce = <T extends (...args: any[]) => any>(callback: T, waitFor: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>): ReturnType<T> => {
        let result: any;
        timeout && clearTimeout(timeout);
        timeout = setTimeout(() => {
            result = callback(...args);
        }, waitFor);
        return result;
    };
};

// use Ref?
export const memoAsync = <T1, TReturn>(func: (arg: T1) => Promise<TReturn>): ((arg: T1) => Promise<TReturn>) => {
    const cache = {} as any;
    return async function (arg: T1) {
        if (cache[arg]) {
            return cache[arg];
        }
        const result = await func(arg);
        cache[arg] = result;
        return result;
    };
};

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

export const isLifestyle = (): boolean => window.location.href.includes("lifestyle");

export const lifestyleIsFree = (pricingResult: Pricing | undefined) =>
    !!pricingResult && pricingResult[LifestyleProduct.lifestyle][PaymentFrequency.yearly] === 0;

export const acceptsNationalAddresses = (pricingResult: Pricing | undefined): boolean =>
    isLifestyle() && lifestyleIsFree(pricingResult);

type ResultOrTimeout<T> = {
    result?: T;
    timeoutMessage?: string;
};

/** `void` is equivalent to `"enforce-timeout"`. */
type OnTimeoutAction = "ignore" | "resolve" | "reject";

/**
 * Snarfed from https://javascript.plainenglish.io/how-to-add-a-timeout-limit-to-asynchronous-javascript-functions-3676d89c186d and modified
 * @param promise 
 * @param timeoutMs 
 * @param onTimeout Callback that (optionally performs side effects, then) returns an instruction when the timeout times out.
    If this instruction is "ignore", the timeout will be disregarded and the function will continue waiting on the original promise.
    If "resolve", the promise will be resolved with a timeout message. If "reject", it will be rejected with a timeout message.
 * @returns a Promise that either a) resolves to an object containing either the original promise's result or a timeout message, or b) is rejected by the original promise.
 */
export const addTimeout = async <T>(
    promise: Promise<T>,
    timeoutMs: number,
    onTimeout: () => OnTimeoutAction,
): Promise<ResultOrTimeout<T>> => {
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeoutMessage = `Timeout of ${timeoutMs} exceeded.`;
    const timeoutPromise = new Promise<ResultOrTimeout<T>>((resolve, reject) => {
        timeoutHandle = setTimeout(() => {
            const onTimeoutAction = onTimeout();
            if (onTimeoutAction === "resolve") {
                resolve({ timeoutMessage });
            }
            if (onTimeoutAction === "reject") {
                reject(timeoutMessage);
            }
        }, timeoutMs);
    });
    const wrappedPromise = promise.then((x) => ({ result: x }));

    return Promise.race([wrappedPromise, timeoutPromise]).then((result) => {
        clearTimeout(timeoutHandle);
        return result;
    });
};

export const fetchWithTimeout = async (url: string, options?: RequestInit, timeoutMs = 15000): Promise<Response> => {
    const controller = new AbortController();
    const fetchPromise = fetch(url, { ...options, signal: controller.signal });
    const { result, timeoutMessage } = await addTimeout(fetchPromise, timeoutMs, () => {
        controller.abort();
        return "reject";
    });
    return timeoutMessage || !result ? Promise.reject(timeoutMessage || result) : Promise.resolve(result);
};
